/**
 * RFC-4180 CSV parsing — pure, dependency-free, unit-tested.
 *
 * Handles quoted fields, escaped quotes (`""`), embedded commas/newlines,
 * CRLF or LF line endings, and a leading UTF-8 BOM. Returns a header row plus
 * data rows as `Record<header, string>`. Empty trailing line is ignored.
 *
 * (XLSX is parsed separately via SheetJS in the API layer, which produces the
 * same `{ headers, rows }` shape so the rest of the pipeline is format-blind.)
 */

export interface ParsedTable {
  headers: string[]
  rows: Array<Record<string, string>>
  /** Raw matrix (excluding header) — handy for duplicate-header columns. */
  matrix: string[][]
}

/** Tokenize CSV text into a matrix of cells (header + data rows). */
export function parseCsvMatrix(text: string): string[][] {
  // Strip BOM.
  if (text.charCodeAt(0) === 0xfeff) text = text.slice(1)

  const rows: string[][] = []
  let row: string[] = []
  let field = ''
  let inQuotes = false
  let i = 0
  const n = text.length

  const pushField = () => {
    row.push(field)
    field = ''
  }
  const pushRow = () => {
    pushField()
    rows.push(row)
    row = []
  }

  while (i < n) {
    const c = text[i]
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"'
          i += 2
          continue
        }
        inQuotes = false
        i++
        continue
      }
      field += c
      i++
      continue
    }
    if (c === '"') {
      inQuotes = true
      i++
      continue
    }
    if (c === ',') {
      pushField()
      i++
      continue
    }
    if (c === '\r') {
      // CRLF or lone CR
      if (text[i + 1] === '\n') i++
      pushRow()
      i++
      continue
    }
    if (c === '\n') {
      pushRow()
      i++
      continue
    }
    field += c
    i++
  }
  // Flush trailing field/row unless the file ended exactly on a newline with no
  // pending content.
  if (field.length > 0 || row.length > 0) pushRow()

  return rows
}

/**
 * Parse CSV text into headers + objects. Duplicate headers are disambiguated
 * with a `__2`, `__3` suffix so wide repeated columns (cbc, cbc) survive.
 */
export function parseCsv(text: string): ParsedTable {
  const matrix = parseCsvMatrix(text)
  if (matrix.length === 0) return { headers: [], rows: [], matrix: [] }

  const rawHeaders = matrix[0].map((h) => h.trim())
  const headers = dedupeHeaders(rawHeaders)
  const dataMatrix = matrix.slice(1)

  const rows = dataMatrix.map((cells) => {
    const obj: Record<string, string> = {}
    headers.forEach((h, idx) => {
      obj[h] = cells[idx] ?? ''
    })
    return obj
  })

  return { headers, rows, matrix: dataMatrix }
}

/** Build a ParsedTable from an already-tokenized matrix (used by the XLSX path). */
export function tableFromMatrix(matrix: string[][]): ParsedTable {
  if (matrix.length === 0) return { headers: [], rows: [], matrix: [] }
  const headers = dedupeHeaders(matrix[0].map((h) => String(h ?? '').trim()))
  const dataMatrix = matrix.slice(1)
  const rows = dataMatrix.map((cells) => {
    const obj: Record<string, string> = {}
    headers.forEach((h, idx) => {
      obj[h] = cells[idx] != null ? String(cells[idx]) : ''
    })
    return obj
  })
  return { headers, rows, matrix: dataMatrix }
}

export function dedupeHeaders(headers: string[]): string[] {
  const seen = new Map<string, number>()
  return headers.map((h, idx) => {
    const base = h || `column_${idx + 1}`
    const count = seen.get(base) ?? 0
    seen.set(base, count + 1)
    return count === 0 ? base : `${base}__${count + 1}`
  })
}
